import React, { useState, useRef, useEffect, useCallback } from 'react';
import Squire from 'squire-rte';
import {
  Box, Stack, IconButton, Tooltip, Divider,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatStrikethroughIcon from '@mui/icons-material/StrikethroughS';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import CodeIcon from '@mui/icons-material/Code';
import { useDialog } from './DialogProvider';

function splitDoc(html) {
  if (!html) return { wrapStart: '', body: '', wrapEnd: '' };
  const m = html.match(/^([\s\S]*?<body[^>]*>)([\s\S]*?)(<\/body>[\s\S]*)$/i);
  if (m) return { wrapStart: m[1], body: m[2], wrapEnd: m[3] };
  return { wrapStart: '', body: html, wrapEnd: '' };
}

function joinDoc(parts, newBody) {
  if (!parts.wrapStart) return newBody;
  return parts.wrapStart + newBody + parts.wrapEnd;
}

function passthroughSanitizer(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<!DOCTYPE html><html><body>${html}</body></html>`,
    'text/html'
  );
  const frag = document.createDocumentFragment();
  while (doc.body.firstChild) frag.appendChild(doc.body.firstChild);
  return frag;
}

export default function RichEmailEditor({ value, onChange, minHeight = 360 }) {
  const dialog = useDialog();
  const containerRef = useRef(null);
  const editorRef = useRef(null);
  const docPartsRef = useRef(splitDoc(value || ''));
  const [showSource, setShowSource] = useState(false);
  const [sourceDraft, setSourceDraft] = useState(docPartsRef.current.body);
  const lastExternalValue = useRef(value);
  const skipNextUpdate = useRef(false);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const editor = new Squire(root, {
      blockTag: 'p',
      sanitizeToDOMFragment: passthroughSanitizer,
    });
    editorRef.current = editor;
    editor.setHTML(docPartsRef.current.body || '<p></p>');

    const handler = () => {
      if (skipNextUpdate.current) {
        skipNextUpdate.current = false;
        return;
      }
      const newValue = joinDoc(docPartsRef.current, editor.getHTML());
      lastExternalValue.current = newValue;
      onChange?.(newValue);
    };
    editor.addEventListener('input', handler);

    return () => {
      try { editor.removeEventListener('input', handler); } catch {}
      try { editor.destroy(); } catch {}
      editorRef.current = null;
    };
  }, []); // eslint-disable-line

  // Sync when value changes from outside (template selection)
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (value === lastExternalValue.current) return;
    lastExternalValue.current = value;
    const parts = splitDoc(value || '');
    docPartsRef.current = parts;
    skipNextUpdate.current = true;
    editor.setHTML(parts.body || '<p></p>');
    setSourceDraft(parts.body);
  }, [value]);

  // source → visual: push edits into Squire
  useEffect(() => {
    if (showSource) return;
    const editor = editorRef.current;
    if (!editor) return;
    skipNextUpdate.current = true;
    editor.setHTML(sourceDraft || '<p></p>');
    docPartsRef.current = { ...docPartsRef.current, body: sourceDraft };
    const newValue = joinDoc(docPartsRef.current, sourceDraft);
    lastExternalValue.current = newValue;
    onChange?.(newValue);
  }, [showSource]); // eslint-disable-line

  const cmd = useCallback((fn) => () => {
    const editor = editorRef.current;
    if (!editor) return;
    fn(editor);
    editor.focus();
  }, []);

  const setLink = useCallback(async () => {
    const editor = editorRef.current;
    if (!editor) return;
    const url = await dialog.prompt({ title: 'Insert link', label: 'URL' });
    if (!url) return;
    editor.makeLink(url);
    editor.focus();
  }, [dialog]);

  const toggleSource = useCallback(() => {
    if (!showSource) {
      const editor = editorRef.current;
      if (editor) setSourceDraft(editor.getHTML());
    }
    setShowSource((s) => !s);
  }, [showSource]);

  return (
    <Box sx={{ border: '1px solid #d1d5db', borderRadius: 1, bgcolor: '#fff' }}>
      <Stack
        direction="row"
        spacing={0.25}
        sx={{
          p: 0.5, bgcolor: '#f7fafc',
          borderBottom: '1px solid #e5e7eb',
          borderRadius: '4px 4px 0 0',
          flexWrap: 'wrap', alignItems: 'center',
        }}
      >
        <Tooltip title="Bold (Ctrl+B)">
          <IconButton size="small" onClick={cmd(e => e.bold())} disabled={showSource}>
            <FormatBoldIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Italic (Ctrl+I)">
          <IconButton size="small" onClick={cmd(e => e.italic())} disabled={showSource}>
            <FormatItalicIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Underline (Ctrl+U)">
          <IconButton size="small" onClick={cmd(e => e.underline())} disabled={showSource}>
            <FormatUnderlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Strikethrough">
          <IconButton size="small" onClick={cmd(e => e.strikethrough())} disabled={showSource}>
            <FormatStrikethroughIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title="Bullet list">
          <IconButton size="small" onClick={cmd(e => e.makeUnorderedList())} disabled={showSource}>
            <FormatListBulletedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Numbered list">
          <IconButton size="small" onClick={cmd(e => e.makeOrderedList())} disabled={showSource}>
            <FormatListNumberedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Blockquote">
          <IconButton size="small" onClick={cmd(e => e.increaseQuoteLevel())} disabled={showSource}>
            <FormatQuoteIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title="Insert link">
          <IconButton size="small" onClick={setLink} disabled={showSource}>
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Remove link">
          <IconButton size="small" onClick={cmd(e => e.removeLink())} disabled={showSource}>
            <LinkOffIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Clear formatting">
          <IconButton size="small" onClick={cmd(e => e.removeAllFormatting())} disabled={showSource}>
            <FormatClearIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title="Undo">
          <IconButton size="small" onClick={cmd(e => e.undo())} disabled={showSource}>
            <UndoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Redo">
          <IconButton size="small" onClick={cmd(e => e.redo())} disabled={showSource}>
            <RedoIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Box sx={{ flex: 1 }} />

        <Tooltip title={showSource ? 'Visual editor' : 'Edit raw HTML'}>
          <IconButton size="small" onClick={toggleSource} color={showSource ? 'primary' : 'default'}>
            <CodeIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box
        ref={containerRef}
        sx={{
          display: showSource ? 'none' : 'block',
          minHeight: minHeight,
          maxHeight: '70vh',
          overflow: 'auto',
          padding: 2,
          fontFamily: 'Arial, Helvetica, sans-serif',
          fontSize: '13px',
          lineHeight: 1.6,
          color: '#444',
          outline: 'none',
          '& *:focus': { outline: 'none' },
          '& img': { maxWidth: '100%', height: 'auto' },
          '& a': { color: 'primary.main' },
          '& blockquote': {
            borderLeft: '3px solid #d1d5db',
            paddingLeft: 1.5,
            color: 'text.secondary',
            margin: '0.5em 0',
          },
          '& table': { borderCollapse: 'collapse' },
        }}
      />

      {showSource && (
        <textarea
          value={sourceDraft}
          onChange={(e) => setSourceDraft(e.target.value)}
          spellCheck={false}
          style={{
            width: '100%',
            minHeight: minHeight,
            maxHeight: '70vh',
            padding: 12,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 13,
            lineHeight: 1.5,
            border: 'none',
            borderRadius: '0 0 4px 4px',
            boxSizing: 'border-box',
            resize: 'vertical',
            outline: 'none',
          }}
        />
      )}
    </Box>
  );
}
